import React, { useEffect, useRef, useState } from 'react';
import { locale } from '../../../common/localization/localizationService';
import * as XLSX from 'xlsx';
import HeaderStep from '../../routeOptimalizationWizard/stepsHeader/HeaderStep';
import {
  faChevronLeft,
  faFile,
  faFlask,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import RoutesImportGrid from './RouteStopsImportGrid';
import { NavLink } from 'react-router-dom';
import routes from '../../../common/routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RouteService from '../../../services/routeService';
import { Toastr } from '../../../utils/Toastr';
import { Spinner2 } from '../../shared/spinner2';
import { IUser } from '../../../models';
import { WarningBanner } from '../../routeAdminLayer/WarningBanner';
import { IImportedRouteStop } from '../../../models/importedRouteStops';

interface IRouteStopsImportProps {
  authentication: IUser;
  impersonatedUser: any;
  setImportedRouteStops: (importedRouteStops: IImportedRouteStop[]) => void;
}

export default function RouteStopsImport(
  props: IRouteStopsImportProps,
): JSX.Element {

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [importedRoutesState, setImportedRoutesState] = useState<
    IImportedRouteStop[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaveAvailable, setIsSaveAvailabe] = useState<boolean>(false);
  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);
  const [warningText, setWaringText] = useState<string>('');

  useEffect(() => {
    setImportedRoutes();
  }, [excelData]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      props.setImportedRouteStops([]);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      props.setImportedRouteStops([]);
    };
  }, []);

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      if (!data) {
        return;
      }

      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json<any>(worksheet, {
        defval: null,
      });

      setExcelData(jsonData);
    };

    reader.readAsBinaryString(file);

    e.target.value = '';
  };

  const parseCoordinate = (value: unknown): number => {
    if (typeof value === 'number') return value;

    if (typeof value === 'string') {
      const normalized = value.trim().replace(',', '.');
      const parsed = Number(normalized);

      if (Number.isNaN(parsed)) {
        throw new Error(`Invalid coordinate value: ${value}`);
      }

      return parsed;
    }

    throw new Error(`Unsupported coordinate type: ${typeof value}`);
  };

  const setImportedRoutes = (): void => {
    const importedRouteStops = excelData.map((row) => {
      const routeStop: IImportedRouteStop = {
        route: row[locale.routeAdminPage._route],
        sequence: row[locale.routeAdminPage._sequence],
        degLat: parseCoordinate(row[locale.routeAdminPage._degLat]),
        degLong: parseCoordinate(row[locale.routeAdminPage._degLong]),
        agreementLineId: row[locale.routeAdminPage._agreementLineId],
        paSystem: row[locale.routeOverview._system],
      };
      return routeStop;
    });
    props.setImportedRouteStops(importedRouteStops);
    setImportedRoutesState(importedRouteStops);
    setIsSaveAvailabe(false);
    setIsWarningVisible(false);
  };

  const checkExistingAgreementLines = async (): Promise<void> => {
    try {
      setIsLoading(true);      
      const validationResponse =
        await RouteService.checkExistingAgreementLines(
          importedRoutesState,
        );
      const nonExistingAgreementLines = validationResponse.nonExistingAgreementLineIds;
      const errorList = validationResponse.errorList;
      if (nonExistingAgreementLines.length === 0 && !errorList?.length) {
        setIsSaveAvailabe(true);
        Toastr.success(locale.routeOverview._importRouteStopsValidationSuccess);
      } else {
        setWaringText(getWarningText(nonExistingAgreementLines));
        errorList?.forEach((err: string) => {
          setWaringText(getValidationErrorText(err));
        });
        setIsWarningVisible(true);
      }
    } catch (err) {
      console.error(err);
      Toastr.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveImportedRouteStops = async (): Promise<void> => {
    try {
      setIsLoading(true);

      await RouteService.saveImportedRouteStops(
        importedRoutesState,
      );
      Toastr.success('Imported routes are saved');
    } catch (err) {
      console.error(err);
      Toastr.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getWarningText = (nonExistingAgreementLines: Array<string>): string => {
    const text = `${locale.routeOverview._nonExistingAgreementLineIdWarningText} ${nonExistingAgreementLines.join(', ')}`;
    
    return text;
  };

   const getValidationErrorText = (err: string): string => {
    
    switch(err) {
      case 'All route lines to import must have the same route id.':
        return locale.routeOverview._validationErrorAllLinesSameRoute;
      case 'No route id specified for the import.':
        return locale.routeOverview._validationErrorNoRouteId;
      case 'Cannot import route stops to a route that already has active route stops.':
        return locale.routeOverview._validationErrorActiveRouteStops;
      case 'Route does not exist.':
        return locale.routeOverview._validationErrorRouteDoesNotExist;
      default:
        return err;
    }
  };

  return (
    <>
      <Spinner2 show={isLoading} />
      <div className="import-route-stops-wrapper">
        <div className="route-overview-header-element text-right mb-3">
          <NavLink
            to={routes.routeAdminPagePath}
            className="btn btn-outline-dark"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </NavLink>
        </div>
        <div className="steps-header">
          <HeaderStep
            title={locale.routeOverview._selectFileLabel}
            buttonText={locale.routeOverview._selectFile}
            buttonClick={() => fileInputRef.current?.click()}
            icon={faFile}
          />
          <HeaderStep
            title={locale.routeOverview._checkNonExistingAgreementLines}
            buttonText={locale.routeOverview._validate}
            buttonClick={checkExistingAgreementLines}
            disabled={excelData.length === 0}
            icon={faFlask}
          />
          <HeaderStep
            title={locale.routeOverview._save}
            buttonText={locale.routeOverview._save}
            buttonClick={saveImportedRouteStops}
            disabled={!isSaveAvailable}
            icon={faSave}
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={onFileSelected}
        />
      </div>
      <div className="route-stop-import-grid-wrapper">
        {excelData.length > 0 && <RoutesImportGrid data={excelData} />}
      </div>
      <WarningBanner hidden={!isWarningVisible} text={warningText} />
    </>
  );
}
